/**
 * Long jump and high jump state machines. Each attempt has its own timeline (sim seconds
 * from the moment the athlete is set on the runway). The run-up reuses the runner sim. Like
 * the 1984 original: Space DOWN fixes the take-off point (and is a foul past the board), the
 * hold sweeps the angle gauge while the athlete coils, Space UP launches the flight with the
 * run-up speed at take-off. Pure and fixed-step like the runner.
 */
import {
  flightAt,
  flightLandingTime,
  highJumpTakeoff,
  longJumpTakeoff,
  type Flight,
} from '../physics.js';
import type { RunKey } from '../input.js';
import {
  createRunner,
  runnerAdvance,
  runnerPress,
  SIM_DT,
  type RunnerConfig,
  type RunnerState,
} from './runner.js';

export type JumpPhase = 'runup' | 'planted' | 'gauge' | 'flight' | 'landed' | 'foul' | 'done';
export type FoulReason = 'board' | 'short' | 'bar' | 'ranThrough' | null;

export interface JumpConfig {
  /** Runner used for the run-up. Distance is just past the board/bar so it never "finishes". */
  run: RunnerConfig;
  /** Degrees per second the gauge sweeps while Space is held. */
  gaugeRate: number;
  /** Height of the centre of mass at take-off (m). */
  comHeight: number;
}

/** Long jump: 40 m runway to the board, sand pit beyond. */
export interface LongJumpConfig extends JumpConfig {
  board: number;
  pitLength: number;
  attempts: number;
}

export const LONG_JUMP: LongJumpConfig = {
  // Under sprint pace: with the cadence cap a perfect jump lands a little over 8 m.
  run: {
    distance: 60,
    topSpeed: 9.9,
    sweetCadence: 10,
    accel: 1.6,
    decel: 2.5,
    stamina: null,
    falseStartWindow: 0,
  },
  gaugeRate: 80,
  comHeight: 1.0,
  board: 40,
  pitLength: 10,
  attempts: 3,
};

/** The foot may overhang the board by this much before it is a foul. */
export const BOARD_TOLERANCE = 0.2;
/** Running this far past the board without pressing Space is a foul. */
export const RUN_THROUGH = 0.6;

/** High jump: straight approach to a bar across the runway, mats behind it. */
export interface HighJumpConfig extends JumpConfig {
  vault?: boolean;
  bar: number;
  /** Height of the landing mats (m). */
  matHeight: number;
  matDepth: number;
  /** The centre of mass must pass this far above the bar (body thickness). */
  clearance: number;
  /** Bar goes up by this after every clearance. */
  step: number;
  /** Consecutive misses that end the event. */
  maxMisses: number;
}

export const HIGH_JUMP: HighJumpConfig = {
  run: {
    distance: 30,
    topSpeed: 8.6,
    sweetCadence: 10,
    accel: 2.2,
    decel: 3,
    stamina: null,
    falseStartWindow: 0,
  },
  gaugeRate: 80,
  comHeight: 1.0,
  bar: 22,
  matHeight: 0.6,
  matDepth: 4,
  clearance: 0.25,
  step: 0.05,
  maxMisses: 3,
};

export const POLE_VAULT: HighJumpConfig = {
  ...HIGH_JUMP,
  vault: true,
  run: { ...HIGH_JUMP.run, topSpeed: 9.5 },
  clearance: 0.15,
  step: 0.1,
};
/** Pole vault: the plant must happen within this of the ideal point, `PV_PLANT_BEFORE_BAR` short of the bar. */
export const PV_PLANT_BEFORE_BAR = 2.8;
export const PV_PLANT_TOLERANCE = 1.5;
/** Pole vault: the second Space press (the push-off) is accepted this long after the plant (s). */
export const PV_PUSH_MIN = 0.5;
export const PV_PUSH_MAX = 1.2;
export const PV_PUSH_IDEAL = 0.85;

interface JumpCore {
  /** Pole vault: Space was pressed on the approach; the pole plants itself at the box. */
  plantArmed: boolean;
  phase: JumpPhase;
  runner: RunnerState;
  /** Sim time within the current attempt. */
  t: number;
  gaugeStart: number | null;
  angle: number;
  flight: Flight | null;
  x: number;
  /** Feet height above the ground (0 while running). */
  y: number;
  /** Centre-of-mass height, for renderers that anchor the body on it. */
  comY: number;
  takeoffX: number | null;
  /** Run-up speed at the moment Space went down. */
  takeoffV: number;
  foul: FoulReason;
}

export interface LongJumpState extends JumpCore {
  attempt: number;
  results: (number | null)[];
  /** Distance of the attempt just completed. */
  mark: number | null;
  best: number | null;
}

export interface HighJumpState extends JumpCore {
  bar: number;
  /** Consecutive misses. */
  misses: number;
  /** All attempts so far. */
  log: { height: number; cleared: boolean }[];
  best: number | null;
  /** Result of the attempt just completed. */
  cleared: boolean | null;
  /** Height the bar was at when it fell (client animation). */
  barFell: boolean;
}

function core(): JumpCore {
  return {
    plantArmed: false,
    phase: 'runup',
    runner: createRunner(0),
    t: 0,
    gaugeStart: null,
    angle: 0,
    flight: null,
    x: 0,
    y: 0,
    comY: 1.0,
    takeoffX: null,
    takeoffV: 0,
    foul: null,
  };
}

/** Space down during the run-up: fix the take-off point and start the gauge. */
function startGauge(s: JumpCore, cfg: JumpConfig, t: number): void {
  runnerAdvance(s.runner, cfg.run, t);
  s.t = t;
  s.x = s.runner.x;
  s.takeoffX = s.runner.x;
  s.takeoffV = s.runner.v;
  s.phase = 'gauge';
  s.gaugeStart = t;
  s.angle = 0;
}

// ------------------------------------------------------------------ long jump
export function createLongJump(): LongJumpState {
  return { ...core(), attempt: 0, results: [], mark: null, best: null };
}

export function jumpPress(s: JumpCore, cfg: JumpConfig, key: RunKey, t: number): void {
  if (s.phase !== 'runup' && s.phase !== 'gauge') return;
  runnerPress(s.runner, cfg.run, key, Math.max(0, t));
}

/** Space pressed (take-off point, gauge starts) or released (fly). */
export function longJumpAction(
  s: LongJumpState,
  cfg: LongJumpConfig,
  down: boolean,
  t: number,
): void {
  if (down && s.phase === 'runup') {
    startGauge(s, cfg, t);
    if (s.takeoffX! > cfg.board + BOARD_TOLERANCE) endLong(s, cfg, null, 'board');
  } else if (!down && s.phase === 'gauge') {
    s.t = Math.max(s.t, t);
    s.angle = Math.min(90, (s.t - (s.gaugeStart ?? s.t)) * cfg.gaugeRate);
    const { vx, vy } = longJumpTakeoff(s.takeoffV, s.angle);
    s.flight = { x0: s.takeoffX ?? s.x, y0: cfg.comHeight, t0: s.t, vx, vy };
    s.phase = 'flight';
  }
}

function endLong(
  s: LongJumpState,
  _cfg: LongJumpConfig,
  mark: number | null,
  foul: FoulReason,
): void {
  s.mark = mark;
  s.foul = foul;
  s.results.push(mark);
  if (mark !== null && (s.best === null || mark > s.best)) s.best = mark;
  s.phase = mark === null ? 'foul' : 'landed';
}

export function longJumpAdvance(s: LongJumpState, cfg: LongJumpConfig, until: number): void {
  while (
    s.t + SIM_DT <= until + 1e-9 &&
    (s.phase === 'runup' || s.phase === 'planted' || s.phase === 'gauge' || s.phase === 'flight')
  ) {
    s.t += SIM_DT;
    if (s.phase === 'runup') {
      runnerAdvance(s.runner, cfg.run, s.t);
      s.x = s.runner.x;
      s.y = 0;
      // Ran through the board without taking off.
      if (s.runner.x > cfg.board + RUN_THROUGH) {
        s.takeoffX = s.runner.x;
        endLong(s, cfg, null, 'board');
      }
    } else if (s.phase === 'gauge') {
      // Coiled at the take-off point; only the gauge moves.
      s.angle = Math.min(90, (s.t - (s.gaugeStart ?? s.t)) * cfg.gaugeRate);
      if (s.t - (s.gaugeStart ?? s.t) > 3) endLong(s, cfg, null, 'short');
    } else if (s.phase === 'flight' && s.flight) {
      const p = flightAt(s.flight, s.t);
      s.x = p.x;
      s.comY = p.y;
      s.y = Math.max(0, p.y - cfg.comHeight);
      const landT = s.flight.t0 + flightLandingTime(s.flight, cfg.comHeight * 0.55);
      if (s.t >= landT) {
        const land = flightAt(s.flight, landT);
        s.x = land.x;
        s.y = 0;
        s.comY = cfg.comHeight * 0.55;
        const dist = Math.round((land.x - cfg.board) * 100) / 100;
        if (dist <= 0) endLong(s, cfg, null, 'short');
        else endLong(s, cfg, Math.min(dist, cfg.pitLength), null);
      }
    }
  }
}

/** Attempt clock ran out: a foul without a jump. */
export function longJumpTimeout(s: LongJumpState): void {
  if (s.phase !== 'runup') return;
  s.results.push(null);
  s.mark = null;
  s.foul = 'board';
  s.phase = 'foul';
}

/** After the landing/foul display: reset for the next attempt or finish the event. */
export function longJumpNext(s: LongJumpState, cfg: LongJumpConfig): void {
  s.attempt++;
  if (s.attempt >= cfg.attempts) {
    s.phase = 'done';
    return;
  }
  Object.assign(s, core(), { attempt: s.attempt, results: s.results, best: s.best, mark: null });
}

// ------------------------------------------------------------------ high jump
export function createHighJump(startHeight: number): HighJumpState {
  return {
    ...core(),
    bar: startHeight,
    misses: 0,
    log: [],
    best: null,
    cleared: null,
    barFell: false,
  };
}

export function highJumpAction(
  s: HighJumpState,
  cfg: HighJumpConfig,
  down: boolean,
  t: number,
): void {
  if (cfg.vault && s.phase === 'planted') {
    // Push off: releasing Space or pressing it again, once the swing has developed. Too
    // early is ignored (keep swinging); too late is handled by the swing running out.
    highJumpAdvance(s, cfg, t);
    if (s.phase !== 'planted') return;
    const hold = t - (s.gaugeStart ?? t);
    if (hold < PV_PUSH_MIN) return;
    if (hold > PV_PUSH_MAX) {
      endHigh(s, false, 'bar');
      return;
    }
    // Height falls off gently either side of the ideal push-off moment.
    const height =
      1.4 +
      ((s.takeoffV * s.takeoffV) / (2 * 9.81)) *
        Math.max(0.35, 1 - 0.6 * Math.abs(hold - PV_PUSH_IDEAL));
    s.angle = hold * 90;
    // The swing carries the vaulter from the plant to just past the bar in about half a
    // second, whatever the plant distance, so the bar is crossed near the top of the arc.
    const vx = Math.max(2.8, (cfg.bar - s.x + 0.8) / 0.5);
    s.flight = { x0: s.x, y0: height, t0: t, vx, vy: 0.5 };
    s.comY = height;
    s.phase = 'flight';
    return;
  }

  if (down && s.phase === 'runup') {
    if (cfg.vault) {
      // Before the box: arm the plant. The pole goes into the box by itself when the
      // athlete gets there (see highJumpAdvance), so timing the press is not required.
      runnerAdvance(s.runner, cfg.run, t);
      s.x = s.runner.x;
      if (s.x < cfg.bar - PV_PLANT_BEFORE_BAR) {
        s.plantArmed = true;
        return;
      }
    }
    startGauge(s, cfg, t);
    if (cfg.vault) {
      if (Math.abs(s.x - (cfg.bar - PV_PLANT_BEFORE_BAR)) > PV_PLANT_TOLERANCE || s.takeoffV < 3) {
        endHigh(s, false, 'ranThrough');
        return;
      }
      s.phase = 'planted';
      return;
    }
    if (s.takeoffX! > cfg.bar - 0.2) endHigh(s, false, 'ranThrough');
  } else if (!down && s.phase === 'gauge') {
    s.t = Math.max(s.t, t);
    s.angle = Math.min(90, (s.t - (s.gaugeStart ?? s.t)) * cfg.gaugeRate);
    const { vx, vy } = highJumpTakeoff(s.takeoffV, s.angle);
    s.flight = { x0: s.takeoffX ?? s.x, y0: cfg.comHeight, t0: s.t, vx, vy };
    s.phase = 'flight';
  }
}

function endHigh(s: HighJumpState, cleared: boolean, foul: FoulReason): void {
  s.cleared = cleared;
  s.foul = foul;
  s.log.push({ height: s.bar, cleared });
  if (cleared) {
    s.misses = 0;
    s.best = s.bar;
    s.phase = 'landed';
  } else {
    s.misses++;
    s.barFell = foul === 'bar' || foul === 'ranThrough';
    s.phase = 'foul';
  }
}

export function highJumpAdvance(s: HighJumpState, cfg: HighJumpConfig, until: number): void {
  while (
    s.t + SIM_DT <= until + 1e-9 &&
    (s.phase === 'runup' || s.phase === 'planted' || s.phase === 'gauge' || s.phase === 'flight')
  ) {
    s.t += SIM_DT;
    if (s.phase === 'runup') {
      runnerAdvance(s.runner, cfg.run, s.t);
      s.x = s.runner.x;
      s.y = 0;
      if (cfg.vault && s.plantArmed && s.runner.x >= cfg.bar - PV_PLANT_BEFORE_BAR) {
        // Armed on the approach: the pole goes into the box as the athlete reaches it.
        startGauge(s, cfg, s.t);
        if (s.takeoffV < 3) endHigh(s, false, 'ranThrough');
        else s.phase = 'planted';
      } else if (s.runner.x > cfg.bar - 0.2) {
        s.takeoffX = s.runner.x;
        endHigh(s, false, 'ranThrough');
      }
    } else if (s.phase === 'planted') {
      const hold = s.t - (s.gaugeStart ?? s.t);
      s.angle = Math.min(90, (hold / 1.1) * 90);
      s.comY = 1 + Math.sin((Math.min(1, hold / 1.1) * Math.PI) / 2) * 3.5;
      if (hold > PV_PUSH_MAX) endHigh(s as HighJumpState, false, 'bar');
    } else if (s.phase === 'gauge') {
      s.angle = Math.min(90, (s.t - (s.gaugeStart ?? s.t)) * cfg.gaugeRate);
      if (s.t - (s.gaugeStart ?? s.t) > 3) endHigh(s, false, 'short');
    } else if (s.phase === 'flight' && s.flight) {
      const prevX = s.x;
      const p = flightAt(s.flight, s.t);
      s.x = p.x;
      s.comY = p.y;
      // Over the mats the body is horizontal, so the feet never drop below the mat top.
      s.y = Math.max(s.x >= cfg.bar ? cfg.matHeight : 0, p.y - cfg.comHeight);
      // Crossing the bar plane: the whole body has to be above the bar.
      if (prevX < cfg.bar && s.x >= cfg.bar) {
        const tCross = s.flight.t0 + (cfg.bar - s.flight.x0) / Math.max(1e-6, s.flight.vx);
        const yCross = flightAt(s.flight, tCross).y;
        if (yCross < s.bar + cfg.clearance) return endHigh(s, false, 'bar');
      }
      // Landing: flat on the mat beyond the bar, or on the ground short of it.
      const beyond = s.x >= cfg.bar;
      const landY = beyond ? cfg.matHeight + cfg.clearance : cfg.comHeight * 0.55;
      if (p.y <= landY && s.t > s.flight.t0 + 0.1) {
        s.y = beyond ? cfg.matHeight : 0;
        s.comY = landY;
        if (!beyond) return endHigh(s, false, 'short');
        endHigh(s, true, null);
      }
    }
  }
}

/** Attempt clock ran out: counts as a miss. */
export function highJumpTimeout(s: HighJumpState): void {
  if (s.phase !== 'runup') return;
  endHigh(s, false, 'ranThrough');
  s.barFell = false;
}

/**
 * After the display pause: raise the bar if cleared, and reset for the next attempt, or
 * finish after three consecutive misses.
 */
export function highJumpNext(s: HighJumpState, cfg: HighJumpConfig, raiseBy = cfg.step): void {
  if (s.misses >= cfg.maxMisses) {
    s.phase = 'done';
    return;
  }
  const bar = s.cleared ? Math.round((s.bar + raiseBy) * 100) / 100 : s.bar;
  Object.assign(s, core(), {
    bar,
    misses: s.misses,
    log: s.log,
    best: s.best,
    cleared: null,
    barFell: false,
  });
}
